import { API_PREFIX } from '@shared/config';
import { httpClient, searchToJson } from '@shared/utils';
import { useUserStore } from '@shared/store';
import { type ApiResult } from '../apis.d';
import type {
  taskList,
  dept,
  taskProgress,
  taskDetail,
  copyStaffList,
  rankDepInfo,
  ICustCheckList,
} from '@feature-task/types';
import type { MomentsListRequest, MomentsListResponse } from '../../types/httpTypes/moments/list';
import { OrgListStaffResponse } from '../../types/httpTypes/task/org/listStaffDo';

// 获取用户id, 本地开发固定写死一个值
const getUserId = () => {
  const isDev = import.meta.env.MODE === 'dev';
  const { userId } = useUserStore().viewStaffInfo;
  // return isDev ? '40000118' : userId;
  return isDev ? '11020017' : userId;
};

// task的接口要加上corpId和staffId
const taskParams = () => {
  return {
    corpId: searchToJson().currentCorpId || import.meta.env.VITE_DEV_SERVER_OPEN_CORP_ID,
    staffId:
      searchToJson().staffId || useUserStore().viewStaffInfo?.staffId || import.meta.env.VITE_MOCK_LOGIN_STAFF_ID,
  };
};
export const getBusinessType = async <U>(): Promise<ApiResult<U>> =>
  await httpClient.post(`${API_PREFIX}api/admin/report/business_type.do`);

export const getDeptList = async <T>(params: T | any): Promise<ApiResult<dept[]>> =>
  await httpClient.post(`/task/api/org/list_owner_dept.do`, { ...params, ...taskParams() });

// 查询客户清单列表
export const getCustCheckList = async <T>(
  params: T | any
): Promise<ApiResult<{ total: number; content: ICustCheckList[] }>> =>
  await httpClient.post(`/task/api/listThirdPartTaskObjectRecord`, { ...params, ...taskParams() });

// 菜单列表，找是否有管理者小结
export const getTaskListHome = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`${API_PREFIX}api/applet/menu/list`, params);

// 任务列表
export const getTaskList = async <T>(params: T | any): Promise<ApiResult<taskList>> =>
  await httpClient.post(`/task/api/list_task.do`, { ...params, ...taskParams() });

// 任务列表右下角，是否参加节日
export const getFestivalSpringId = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`${API_PREFIX}api/special_column/specific_item`, { params });

// 获取任务类型
export const getTaskTypeList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/list_type.do`, { ...params, ...taskParams() });
//任务详情列表
export const getTaskDetail = async <T>(params: T | any): Promise<ApiResult<taskDetail>> =>
  await httpClient.post(`/task/api/get_task_detail.do`, { ...params, ...taskParams() });

// 查询抄送任务员工列表
export const getCopyTaskDetail = async <T>(params: T | any): Promise<ApiResult<copyStaffList>> =>
  await httpClient.post(`/task/api/list_cc_task_staff.do`, { ...params, ...taskParams() });

// 获取任务进度
export const getTaskProgress = async <T>(params: T | any): Promise<ApiResult<taskProgress>> =>
  await httpClient.post(`/task/api/get_task_progress.do`, { ...params, ...taskParams() });

// 更新任务进度
export const updateTaskProgress = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/update_task_progress.do`, { ...params, ...taskParams() });

// 结束任务
export const endTask = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/end_task.do`, { ...params, ...taskParams() });

// 抄送任务
export const setCopyStaffList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/cc_task.do`, { ...params, ...taskParams() });

// 任务管理删除标签
export const delTaskBatchSendTag = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`${API_PREFIX}api/applet/task/manage/del_tag.do`, { ...params });

// 部门任务完成排名
export const getDeptRank = async <T>(params: T | any): Promise<ApiResult<rankDepInfo>> =>
  await httpClient.post(`/task/api/get_ranked_by_dept.do`, { ...params, ...taskParams() });

// 获取员工列表（无权限控制）
export const deptStaffList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/org/list_dept_staff.do`, { ...params, ...taskParams() });

// 抄送任务搜索员工（模糊搜索）
export const staffList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/org/list_staff.do`, { ...params, ...taskParams() });

// 获取员工列表
export const ownerStaffList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/org/list_owner_staff.do`, { ...params, ...taskParams() });

// 获取组织架构部门（无权限限制）
export const childDeptList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/org/list_child_dept.do`, { ...params, ...taskParams() });

// 获取组织架构部门
export const ownerChildDeptList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/org/list_owner_child_dept.do`, { ...params, ...taskParams() });

// 获取全部职位列表
export const allPositionList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/org/list_all_position.do`, { ...params, ...taskParams() });

// 获取职位列表
export const ownerPositionList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/org/list_owner_position.do`, { ...params, ...taskParams() });

// 获取员工标签
export const getStaffTag = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/tag/list_all_tag.do`, { ...params, ...taskParams() });

// 根据标签id获取标签部门
export const ownerDeptByTag = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/tag/list_owner_dept.do`, { ...params, ...taskParams() });

// 下发任务
export const taskPublish = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/send_task.do`, { ...params, ...taskParams() });

// 营销任务：任务下发页-获取任务指定完成方式列表
export const taskShareChannel = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/get_task_share_channel.do`, { ...params, ...taskParams() });

// 获取任务拆分情况
export const taskSplitDetail = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/get_task_split_detail.do`, { ...params, ...taskParams() });

// 指定协同人
export const assignSynergy = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/assign_synergy.do`, { ...params, ...taskParams() });
// 查询任务可催办员工搜索列表
export const taskUrgeSearchList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/list_urge_staff.do`, { ...params, ...taskParams() });

// 发送任务催办通知
export const taskUrgeNotice = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/urge_task_notice.do`, { ...params, ...taskParams() });

// 查询任务可催办员工列表
export const taskUrgeStaffList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/list_urge_task_staff.do`, { ...params, ...taskParams() });

// 查询晚报id
export const findLastEveningPaper = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`/caizhi_miniapi/api/finance/evening_paper/find_last_evening_paper`, { params });
// 查询朋友圈神器一级导航
export const momentsScene = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`/caizhi_miniapi/api/moments/scene`, { params });
// 查询朋友圈神器二级导航
export const momentsClassify = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`/caizhi_miniapi/api/moments/classify`, { params });
// 查询朋友圈神器列表
export const momentsList = async (params: TLooseObj): Promise<ApiResult<TLooseObj>> =>
  await httpClient.get(`/caizhi_miniapi/api/moments/list`, { params });
// export const momentsList = async (params: TLooseObj): Promise<ApiResult<MomentsListResponse['retdata']>> =>
//   await httpClient.get(`/caizhi_miniapi/api/moments/list`, { params });
// 查询节点关怀一级导航
export const careSceneTypeList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`/caizhi_miniapi/api/careScene/templates/typeList`, { params });
// 查询节点关怀列表
export const careSceneList = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`/caizhi_miniapi/api/careScene/templates/list`, { params });

// 选择类型新增常用类型
export const taskCommonTypes = async <U>(): Promise<ApiResult<U>> =>
  await httpClient.get(`/task/api/list-common-task-types`, {
    headers: {
      isHideError: true,
    },
  });

/** 获取配置的活动信息，首页顶部列表icon信息 */
export const getCmtCommunityRmp = async <T>(params: T | any): Promise<ApiResult<any>> =>
  await httpClient.get(`/cmt_miniapi/api/community/rmp/get`, { params });

// 浦发银行判断
export const getIsPfBank = async (params = {}): Promise<ApiResult<boolean>> =>
  await httpClient.get(`${API_PREFIX}api/applet/station_card/isPfBank`, params);

// 浦发-浦发任务完成（任务完成和保存反馈共用接口，由type参数区分）
export const spdbTaskFeedback = async <T, U>(params: T | any): Promise<ApiResult<U>> => {
  const { corpId } = taskParams();
  return await httpClient.post(`/task/api/spdbTaskFeedback`, { userId: getUserId(), corpId, ...params });
};

// 浦发-任务类型
export const spdbTaskType = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`/task/api/spdbTaskType`, params);

// 浦发-任务列表
export const spdbTaskList = async <T, U>(params: T | any): Promise<ApiResult<U>> => {
  const { corpId } = taskParams();
  return await httpClient.post(`/task/api/spdbTaskList`, { userId: getUserId(), corpId, ...params });
};
// 浦发-任务详情
export const spdbTaskDetail = async <T, U>(params: T | any): Promise<ApiResult<U>> => {
  // userId是经理在企微的账号  staffId是经理在员工表的唯一标识
  const { corpId } = taskParams();
  return await httpClient.post(`/task/api/spdbTaskDetail`, { userId: getUserId(), corpId, ...params });
};

// 浦发任务详情页面跳转链接参数--appId和域名--查询
export const getSpdbSysParams = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.get(`/task/api/spdbTask/systemParam`, {});

// 浦发任务按钮点击事件
export const taskClickReport = async <T, U>(params: T | any): Promise<ApiResult<U>> =>
  await httpClient.post(`/task/api/spdbTaskButton/buriedPoint`, params);

// 浦发任务--获取活动详情
export const getBannerDetail = async <T, U>(params: T | any): Promise<ApiResult<U>> => {
  return await httpClient.post(`/caizhi_miniapi/api/applet/banner/detail.do`, params);
};
